import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { API, AuthContext } from '@/App';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, DollarSign, Mail, Building2, Megaphone, Users, MessageSquare, Trash2, Ban, UserCheck, Calendar, Briefcase, Target, BarChart3, Send, Bell, TrendingUp, PieChart, Edit, UserPlus, BookOpen } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AdminPanel = () => {
  const { user, token } = React.useContext(AuthContext);
  const navigate = useNavigate();
  const [pendingPartnerships, setPendingPartnerships] = useState([]);
  const [pendingAds, setPendingAds] = useState([]);
  const [allAds, setAllAds] = useState([]);
  const [adFilter, setAdFilter] = useState('all'); // 'all', 'pending', 'active', 'expired'
  const [partnerContacts, setPartnerContacts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [memberTestimonials, setMemberTestimonials] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [allOpportunities, setAllOpportunities] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testimonialDialog, setTestimonialDialog] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({ quote: '', author: '', role: '', order: 0 });
  const [announcementDialog, setAnnouncementDialog] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', message: '', type: 'info', expires_at: '' });
  const [emailBlastDialog, setEmailBlastDialog] = useState(false);
  const [emailForm, setEmailForm] = useState({ subject: '', message: '', target_tier: '' });
  const [employees, setEmployees] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [employeeDialog, setEmployeeDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [sendInvitationNow, setSendInvitationNow] = useState(false);
  const [employeeForm, setEmployeeForm] = useState({ 
    full_name: '', 
    email: '', 
    position: '',
    role: 'employee',
    department: '', 
    phone: '', 
    start_date: '', 
    salary: '', 
    status: 'active',
    permissions: [],
    is_super_admin: false
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchAdminData();
  }, [user]);

  // Poll email stats every 60s so the sidebar dot + widget reflect failures in near-real-time
  useEffect(() => {
    if (user?.role !== 'admin' || !token) return;
    const interval = setInterval(() => {
      fetchEmailStats();
    }, 60000);
    return () => clearInterval(interval);
  }, [user, token]);

  const fetchAdminData = async () => {
    setLoading(true);
    try {
      const [partnershipsRes, adsRes, allAdsRes, contactsRes, usersRes, testimonialsRes, memberTestimonialsRes, eventsRes, businessesRes, opportunitiesRes, analyticsRes, announcementsRes, employeesRes] = await Promise.all([
        axios.get(`${API}/admin/partnerships/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch partnerships:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/admin/advertisements/pending`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch advertisements:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/admin/advertisements/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch all advertisements:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/admin/partner-contacts`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch partner contacts:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch users:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/admin/testimonials/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch testimonials:', err.response?.status, err.response?.data);
          toast.error('Failed to load testimonials - authentication error');
          return { data: [] };
        }),
        axios.get(`${API}/admin/member-testimonials/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch member testimonials:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch events:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/businesses`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch businesses:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/opportunities`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch opportunities:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch analytics:', err.response?.status, err.response?.data);
          toast.error('Failed to load analytics - authentication error');
          return { data: null };
        }),
        axios.get(`${API}/admin/announcements/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch announcements:', err.response?.status, err.response?.data);
          return { data: [] };
        }),
        axios.get(`${API}/admin/employees`, { headers: { Authorization: `Bearer ${token}` } }).catch(err => {
          console.error('Failed to fetch employees:', err.response?.status, err.response?.data);
          return { data: [] };
        })
      ]);

      setPendingPartnerships(partnershipsRes.data);
      setPendingAds(adsRes.data);
      setAllAds(allAdsRes.data);
      setPartnerContacts(contactsRes.data);
      setAllUsers(usersRes.data);
      setTestimonials(testimonialsRes.data);
      setMemberTestimonials(memberTestimonialsRes.data);
      setAllEvents(eventsRes.data);
      setAllBusinesses(businessesRes.data);
      setAllOpportunities(opportunitiesRes.data);
      setAnalytics(analyticsRes.data);
      setAnnouncements(announcementsRes.data);
      setEmployees(employeesRes.data);

      // Fetch email delivery stats (non-blocking, separate so it can refresh independently)
      fetchEmailStats();
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  // Selective fetch functions for better performance
  const fetchPartnerships = async () => {
    try {
      const res = await axios.get(`${API}/admin/partnerships/pending`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingPartnerships(res.data);
    } catch (err) {
      console.error('Failed to fetch partnerships:', err);
    }
  };

  const fetchAdvertisements = async () => {
    try {
      const res = await axios.get(`${API}/admin/advertisements/pending`, { headers: { Authorization: `Bearer ${token}` } });
      setPendingAds(res.data);
    } catch (err) {
      console.error('Failed to fetch advertisements:', err);
    }
  };

  const fetchEmailStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/email/stats`, { headers: { Authorization: `Bearer ${token}` } });
      setEmailStats(res.data);
    } catch (err) {
      console.error('Failed to fetch email stats:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setAllUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const res = await axios.get(`${API}/admin/testimonials/all`, { headers: { Authorization: `Bearer ${token}` } });
      setTestimonials(res.data);
    } catch (err) {
      console.error('Failed to fetch testimonials:', err);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${token}` } });
      setAllEvents(res.data);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  };

  const fetchBusinesses = async () => {
    try {
      const res = await axios.get(`${API}/businesses`, { headers: { Authorization: `Bearer ${token}` } });
      setAllBusinesses(res.data);
    } catch (err) {
      console.error('Failed to fetch businesses:', err);
    }
  };

  const fetchOpportunities = async () => {
    try {
      const res = await axios.get(`${API}/opportunities`, { headers: { Authorization: `Bearer ${token}` } });
      setAllOpportunities(res.data);
    } catch (err) {
      console.error('Failed to fetch opportunities:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/admin/employees`, { headers: { Authorization: `Bearer ${token}` } });
      setEmployees(res.data);
    } catch (err) {
      console.error('Failed to fetch employees:', err);
    }
  };

  const approvePartnership = async (partnershipId) => {
    try {
      await axios.post(`${API}/admin/partnerships/${partnershipId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partnership approved!');
      fetchPartnerships(); // Only refresh partnerships
    } catch (error) {
      toast.error('Failed to approve partnership');
    }
  };

  const markPartnershipPaid = async (partnershipId) => {
    try {
      await axios.post(`${API}/admin/partnerships/${partnershipId}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partnership marked as paid!');
      fetchPartnerships(); // Only refresh partnerships
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const approveAdvertisement = async (adId) => {
    try {
      await axios.post(`${API}/admin/advertisements/${adId}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Advertisement approved!');
      fetchAdvertisements(); // Only refresh advertisements
    } catch (error) {
      toast.error('Failed to approve advertisement');
    }
  };

  const markAdPaid = async (adId) => {
    try {
      await axios.post(`${API}/admin/advertisements/${adId}/mark-paid`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Advertisement marked as paid!');
      fetchAdvertisements(); // Only refresh advertisements
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      pending: 'bg-yellow-100 text-yellow-800',
      pending_payment: 'bg-orange-100 text-orange-800',
      active: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return variants[status] || 'bg-gray-100 text-gray-800';
  };

  const approveMemberTestimonial = async (id) => {
    try {
      await axios.put(`${API}/admin/member-testimonials/${id}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member testimonial approved successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to approve member testimonial:', error);
      toast.error('Failed to approve member testimonial');
    }
  };

  const deleteMemberTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this member testimonial?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/member-testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Member testimonial deleted successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete member testimonial:', error);
      toast.error('Failed to delete member testimonial');
    }
  };

  const changeUserRole = async (email, newRole) => {
    try {
      await axios.post(`${API}/admin/grant-role?email=${encodeURIComponent(email)}&role=${newRole}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Role updated to ${newRole} for ${email}`);
      fetchUsers(); // Only refresh users
    } catch (error) {
      console.error('Failed to update user role:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user role');
    }
  };

  const deleteUser = async (userId, userName) => {
    try {
      const confirmed = window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`);
      if (!confirmed) {
        console.log('Delete cancelled by user');
        return;
      }
    } catch (confirmError) {
      console.error('Confirmation dialog error:', confirmError);
      toast.error('Unable to show confirmation dialog. Please try in production.');
      return;
    }
    
    try {
      console.log('Attempting to delete user:', userId);
      const response = await axios.delete(`${API}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Delete response:', response);
      toast.success('User deleted successfully');
      fetchUsers(); // Only refresh users
    } catch (error) {
      console.error('Failed to delete user:', error);
      console.error('Error response:', error.response);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === allUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(allUsers.map(u => u.id));
    }
  };

  const toggleUserSelect = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const bulkDeleteUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }

    try {
      const confirmed = window.confirm(`Are you sure you want to delete ${selectedUsers.length} selected user(s)? This action cannot be undone.`);
      if (!confirmed) {
        console.log('Bulk delete cancelled by user');
        return;
      }
    } catch (confirmError) {
      console.error('Confirmation dialog error:', confirmError);
      toast.error('Unable to show confirmation dialog. Please try in production.');
      return;
    }

    try {
      console.log('Attempting to delete users:', selectedUsers);
      const results = await Promise.allSettled(
        selectedUsers.map(userId =>
          axios.delete(`${API}/admin/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      
      const successful = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      
      console.log(`Bulk delete results: ${successful} successful, ${failed} failed`);
      
      if (successful > 0) {
        toast.success(`${successful} user(s) deleted successfully${failed > 0 ? `, ${failed} failed` : ''}`);
      } else {
        toast.error('Failed to delete users');
      }
      
      setSelectedUsers([]);
      fetchUsers(); // Only refresh users
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error('Failed to delete some users');
      fetchUsers(); // Only refresh users
    }
  };

  const bulkSuspendUsers = async (suspend = true) => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }

    const action = suspend ? 'suspend' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} ${selectedUsers.length} selected user(s)?`)) {
      return;
    }

    try {
      await Promise.all(
        selectedUsers.map(userId =>
          axios.put(`${API}/admin/users/${userId}/suspend?suspend=${suspend}`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          })
        )
      );
      toast.success(`${selectedUsers.length} user(s) ${action}d successfully`);
      setSelectedUsers([]);
      fetchUsers(); // Only refresh users
    } catch (error) {
      toast.error(`Failed to ${action} some users`);
      fetchUsers(); // Only refresh users
    }
  };

  const suspendUser = async (userId, userName, currentlySuspended) => {
    const action = currentlySuspended ? 'unsuspend' : 'suspend';
    if (!window.confirm(`Are you sure you want to ${action} user "${userName}"?`)) {
      return;
    }
    
    try {
      await axios.put(`${API}/admin/users/${userId}/suspend?suspend=${!currentlySuspended}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`User ${action}ed successfully`);
      fetchUsers(); // Only refresh users
    } catch (error) {
      console.error(`Failed to ${action} user:`, error);
      toast.error(error.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const handleTestimonialSubmit = async () => {
    try {
      let response;
      if (editingTestimonial) {
        response = await axios.put(`${API}/admin/testimonials/${editingTestimonial.id}`, testimonialForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`${API}/admin/testimonials`, testimonialForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Check if response indicates success
      if (response.data && response.data.success) {
        toast.success(editingTestimonial ? 'Testimonial updated successfully' : 'Testimonial created successfully');
        setTestimonialDialog(false);
        setEditingTestimonial(null);
        setTestimonialForm({ quote: '', author: '', role: '', order: 0 });
        fetchAdminData();
      } else {
        toast.error('Failed to save testimonial');
      }
    } catch (error) {
      console.error('Failed to save testimonial:', error);
      if (error.response?.status === 422) {
        toast.error('Please fill in all required fields');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to perform this action');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to save testimonial');
      }
    }
  };

  const approveTestimonial = async (id, approve) => {
    try {
      await axios.put(`${API}/admin/testimonials/${id}/approve?approved=${approve}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Testimonial ${approve ? 'approved' : 'rejected'} successfully`);
      fetchAdminData();
    } catch (error) {
      console.error('Failed to approve testimonial:', error);
      toast.error('Failed to approve testimonial');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/testimonials/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial deleted successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete testimonial:', error);
      toast.error('Failed to delete testimonial');
    }
  };

  const deleteEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`Are you sure you want to delete event "${eventTitle}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Event deleted successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete event:', error);
      toast.error('Failed to delete event');
    }
  };

  const deleteBusiness = async (businessId, businessName) => {
    if (!window.confirm(`Are you sure you want to delete business "${businessName}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/businesses/${businessId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Business deleted successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete business:', error);
      toast.error('Failed to delete business');
    }
  };

  const deleteOpportunity = async (opportunityId, opportunityTitle) => {
    if (!window.confirm(`Are you sure you want to delete opportunity "${opportunityTitle}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/opportunities/${opportunityId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Opportunity deleted successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete opportunity:', error);
      toast.error('Failed to delete opportunity');
    }
  };

  const deletePartnership = async (partnershipId, partnerName) => {
    if (!window.confirm(`Are you sure you want to delete partnership with "${partnerName}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/partnerships/${partnershipId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Partnership deleted successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete partnership:', error);
      toast.error('Failed to delete partnership');
    }
  };

  const deleteAdvertisement = async (adId, adTitle) => {
    if (!window.confirm(`Are you sure you want to delete advertisement "${adTitle}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/advertisements/${adId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Advertisement deleted successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete advertisement:', error);
      toast.error('Failed to delete advertisement');
    }
  };

  const deletePartnerContact = async (contactId, contactName) => {
    if (!window.confirm(`Are you sure you want to delete inquiry from "${contactName}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/partner-contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inquiry deleted successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete inquiry:', error);
      toast.error('Failed to delete inquiry');
    }
  };

  const createAnnouncement = async () => {
    try {
      await axios.post(`${API}/admin/announcements`, announcementForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Announcement created successfully');
      setAnnouncementDialog(false);
      setAnnouncementForm({ title: '', message: '', type: 'info', expires_at: '' });
      fetchAdminData();
    } catch (error) {
      console.error('Failed to create announcement:', error);
      toast.error('Failed to create announcement');
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/announcements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Announcement deleted successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete announcement:', error);
      toast.error('Failed to delete announcement');
    }
  };

  // Employee Management Functions
  const openEmployeeDialog = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setEmployeeForm({
        full_name: employee.full_name || '',
        email: employee.email || '',
        position: employee.position || '',
        role: employee.role || 'employee',
        department: employee.department || '',
        phone: employee.phone || '',
        start_date: employee.start_date || '',
        salary: employee.salary || '',
        status: employee.status || 'active',
        permissions: employee.permissions || []
      });
    } else {
      setEditingEmployee(null);
      setEmployeeForm({
        full_name: '',
        email: '',
        position: '',
        role: 'employee',
        department: '',
        phone: '',
        start_date: '',
        salary: '',
        status: 'active',
        permissions: []
      });
    }
    setEmployeeDialog(true);
  };

  const saveEmployee = async () => {
    try {
      if (editingEmployee) {
        // Update existing employee
        await axios.put(`${API}/admin/employees/${editingEmployee.id}`, employeeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Employee updated successfully');
      } else {
        // Create new employee
        await axios.post(`${API}/admin/employees?send_invitation=${sendInvitationNow}`, employeeForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (sendInvitationNow) {
          toast.success('Employee created and invitation sent!');
        } else {
          toast.success('Employee created successfully');
        }
      }
      setEmployeeDialog(false);
      setEditingEmployee(null);
      setSendInvitationNow(false);
      setEmployeeForm({
        full_name: '',
        email: '',
        position: '',
        role: 'employee',
        department: '',
        phone: '',
        start_date: '',
        salary: '',
        status: 'active',
        permissions: []
      });
      fetchAdminData();
    } catch (error) {
      console.error('Failed to save employee:', error);
      toast.error(error.response?.data?.detail || 'Failed to save employee');
    }
  };

  const deleteEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete employee "${employeeName}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/employees/${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Employee deleted successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to delete employee:', error);
      toast.error('Failed to delete employee');
    }
  };

  const sendInvitation = async (employeeId, employeeName, employeeEmail) => {
    if (!window.confirm(`Send invitation email to ${employeeName} (${employeeEmail})?`)) {
      return;
    }
    
    try {
      await axios.post(`${API}/admin/employees/${employeeId}/send-invitation`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Invitation sent to ${employeeEmail}!`);
      fetchAdminData();
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const sendEmailBlast = async () => {
    if (!window.confirm(`Send email to ${emailForm.target_tier || 'all'} members?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/email-blast`, emailForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Email sent to ${response.data.sent} recipients!`);
      setEmailBlastDialog(false);
      setEmailForm({ subject: '', message: '', target_tier: '' });
    } catch (error) {
      console.error('Failed to send email blast:', error);
      toast.error(error.response?.data?.detail || 'Failed to send email blast');
    }
  };

  const exportUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/export/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const blob = new Blob([response.data.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      toast.success('Users exported successfully');
    } catch (error) {
      console.error('Failed to export users:', error);
      toast.error('Failed to export users');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
        <Navigation />
        <div className="container mx-auto px-4 py-8 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading admin panel...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 100%)' }}>
      <Navigation />
      <div className="container mx-auto px-4 py-8" data-testid="admin-panel">
        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10 text-rose-600" />
            <div>
              <h1 className="text-4xl font-bold" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                Admin Panel
              </h1>
              <p className="text-gray-600">Manage partnerships, advertisements, and inquiries</p>
            </div>
          </div>
          <a
            href="/admin/guide"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-rose-100 text-rose-800 hover:bg-rose-200 text-sm font-semibold transition-colors flex-shrink-0"
            data-testid="admin-panel-guide-link"
          >
            <BookOpen className="w-4 h-4" />
            Admin Guide
          </a>
        </div>

        <Tabs defaultValue="analytics" className="space-y-6">
          <TabsList className="glass grid w-full grid-cols-6 lg:grid-cols-11 gap-1">
            <TabsTrigger value="analytics" className="flex items-center gap-1 text-xs">
              <BarChart3 className="w-3 h-3" />
              Analytics
            </TabsTrigger>
            <TabsTrigger value="communications" className="flex items-center gap-1 text-xs relative" data-testid="tab-communications">
              <Send className="w-3 h-3" />
              Communications
              {emailStats?.last_24h?.failed > 0 && (
                <span
                  className="absolute -top-1 -right-1 flex h-2.5 w-2.5"
                  data-testid="email-failure-dot"
                  title={`${emailStats.last_24h.failed} email failure(s) in the last 24h`}
                >
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-600"></span>
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="partnerships" className="flex items-center gap-1 text-xs">
              <Building2 className="w-3 h-3" />
              Partners ({pendingPartnerships.length})
            </TabsTrigger>
            <TabsTrigger value="advertisements" className="flex items-center gap-1 text-xs">
              <Megaphone className="w-3 h-3" />
              Ads ({pendingAds.length})
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-1 text-xs">
              <Mail className="w-3 h-3" />
              Inquiries ({partnerContacts.length})
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-1 text-xs">
              <Users className="w-3 h-3" />
              Users ({allUsers.length})
            </TabsTrigger>
            <TabsTrigger value="employees" className="flex items-center gap-1 text-xs">
              <UserPlus className="w-3 h-3" />
              Employees ({employees.length})
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="flex items-center gap-1 text-xs">
              <MessageSquare className="w-3 h-3" />
              Testimonials ({testimonials.length})
            </TabsTrigger>
            <TabsTrigger value="member-testimonials" className="flex items-center gap-1 text-xs">
              <Users className="w-3 h-3" />
              Member Reviews ({memberTestimonials.length})
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-1 text-xs">
              <Calendar className="w-3 h-3" />
              Events ({allEvents.length})
            </TabsTrigger>
            <TabsTrigger value="businesses" className="flex items-center gap-1 text-xs">
              <Briefcase className="w-3 h-3" />
              Businesses ({allBusinesses.length})
            </TabsTrigger>
            <TabsTrigger value="opportunities" className="flex items-center gap-1 text-xs">
              <Target className="w-3 h-3" />
              Opportunities ({allOpportunities.length})
            </TabsTrigger>
          </TabsList>

          {/* Analytics Dashboard */}
          <TabsContent value="analytics">
            {analytics ? (
              <div className="space-y-6">
                {/* Key Metrics Row */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Users className="w-4 h-4 text-rose-600" />
                        Total Users
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-rose-600">{analytics.users.total}</div>
                      <p className="text-xs text-green-600 mt-1">
                        <TrendingUp className="w-3 h-3 inline mr-1" />
                        +{analytics.users.recent_signups} in last 30 days
                      </p>
                    </CardContent>
                  </Card>

                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Calendar className="w-4 h-4 text-blue-600" />
                        Events
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-blue-600">{analytics.events.total}</div>
                      <p className="text-xs text-gray-600 mt-1">{analytics.events.upcoming} upcoming</p>
                    </CardContent>
                  </Card>

                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Briefcase className="w-4 h-4 text-purple-600" />
                        Businesses
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-purple-600">{analytics.businesses}</div>
                      <p className="text-xs text-gray-600 mt-1">Total listings</p>
                    </CardContent>
                  </Card>

                  <Card className="glass">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Target className="w-4 h-4 text-orange-600" />
                        Opportunities
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-3xl font-bold text-orange-600">{analytics.opportunities}</div>
                      <p className="text-xs text-gray-600 mt-1">Total posted</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* User Status Breakdown */}
                  <Card className="glass">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="w-5 h-5" />
                        User Status Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={[
                          { status: 'Active', count: analytics.users.active },
                          { status: 'Suspended', count: analytics.users.suspended },
                          { status: 'New (30d)', count: analytics.users.recent_signups }
                        ]}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="status" />
                          <YAxis />
                          <Tooltip />
                          <Bar dataKey="count" fill="#fb7185" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Membership Tiers Distribution */}
                  <Card className="glass">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <PieChart className="w-5 h-5" />
                        Membership Tiers
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <RechartsPieChart>
                          <Pie
                            data={Object.entries(analytics.membership_tiers).map(([tier, count]) => ({
                              name: tier.charAt(0).toUpperCase() + tier.slice(1),
                              value: count
                            }))}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {Object.keys(analytics.membership_tiers).map((tier, index) => {
                              const colors = ['#fb7185', '#a78bfa', '#60a5fa', '#34d399'];
                              return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                            })}
                          </Pie>
                          <Tooltip />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Industries */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle>Top Industries</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={analytics.top_industries} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis dataKey="industry" type="category" width={150} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#fb7185" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card className="glass">
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 flex-wrap">
                      <Button onClick={exportUsers} variant="outline" className="gap-2">
                        📥 Export Users CSV
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="glass">
                <CardContent className="py-12 text-center">
                  <p className="text-gray-600">Loading analytics...</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Communications */}
          <TabsContent value="communications">
            <div className="space-y-6">
              {/* Email Delivery Stats Widget */}
              <Card className="glass" data-testid="email-delivery-widget">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="w-5 h-5" />
                        Email Delivery
                      </CardTitle>
                      <CardDescription>
                        Live stats from {emailStats?.provider || 'email provider'} · Sender: {emailStats?.sender_email || '-'}
                      </CardDescription>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchEmailStats}
                      data-testid="email-stats-refresh-btn"
                    >
                      Refresh
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {!emailStats ? (
                    <p className="text-sm text-gray-500">Loading email stats…</p>
                  ) : (
                    <>
                      {/* Alert banner if any recent failures */}
                      {emailStats.last_24h?.failed > 0 && (
                        <div
                          className="mb-4 p-3 rounded-lg border border-red-300 bg-red-50 flex items-start gap-2"
                          data-testid="email-failure-alert"
                        >
                          <XCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-semibold text-red-800">
                              {emailStats.last_24h.failed} email{emailStats.last_24h.failed > 1 ? 's' : ''} failed in the last 24h
                            </p>
                            <p className="text-red-700">
                              Check provider quota, API key, or sender verification.
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="p-4 rounded-lg bg-green-50 border border-green-100" data-testid="email-stat-24h-sent">
                          <p className="text-xs text-green-700 uppercase tracking-wide">Sent (24h)</p>
                          <p className="text-2xl font-bold text-green-800">{emailStats.last_24h?.sent ?? 0}</p>
                        </div>
                        <div className="p-4 rounded-lg bg-red-50 border border-red-100" data-testid="email-stat-24h-failed">
                          <p className="text-xs text-red-700 uppercase tracking-wide">Failed (24h)</p>
                          <p className="text-2xl font-bold text-red-800">{emailStats.last_24h?.failed ?? 0}</p>
                        </div>
                        <div className="p-4 rounded-lg bg-rose-50 border border-rose-100" data-testid="email-stat-7d-sent">
                          <p className="text-xs text-rose-700 uppercase tracking-wide">Sent (7 days)</p>
                          <p className="text-2xl font-bold text-rose-800">{emailStats.last_7d?.sent ?? 0}</p>
                        </div>
                        <div className="p-4 rounded-lg bg-orange-50 border border-orange-100" data-testid="email-stat-7d-failed">
                          <p className="text-xs text-orange-700 uppercase tracking-wide">Failed (7 days)</p>
                          <p className="text-2xl font-bold text-orange-800">{emailStats.last_7d?.failed ?? 0}</p>
                        </div>
                      </div>

                      {emailStats.recent_failures?.length > 0 && (
                        <details className="mb-3" data-testid="email-recent-failures">
                          <summary className="cursor-pointer text-sm font-medium text-red-700">
                            Recent failures ({emailStats.recent_failures.length})
                          </summary>
                          <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg divide-y">
                            {emailStats.recent_failures.map((f, i) => (
                              <div key={i} className="p-2 text-xs">
                                <p className="font-mono text-gray-600">{new Date(f.created_at).toLocaleString()}</p>
                                <p><span className="font-semibold">To:</span> {(f.to || []).join(', ')}</p>
                                <p className="truncate"><span className="font-semibold">Subject:</span> {f.subject}</p>
                                <p className="text-red-700 break-all"><span className="font-semibold">Error:</span> {f.error}</p>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}

                      {emailStats.recent_sent?.length > 0 && (
                        <details data-testid="email-recent-sent">
                          <summary className="cursor-pointer text-sm font-medium text-gray-700">
                            Recent sent ({emailStats.recent_sent.length})
                          </summary>
                          <div className="mt-2 max-h-48 overflow-y-auto border rounded-lg divide-y">
                            {emailStats.recent_sent.map((s, i) => (
                              <div key={i} className="p-2 text-xs flex items-start gap-2">
                                <CheckCircle className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="font-mono text-gray-500">{new Date(s.created_at).toLocaleString()}</p>
                                  <p className="truncate"><span className="text-gray-600">To:</span> {(s.to || []).join(', ')}</p>
                                  <p className="truncate"><span className="text-gray-600">Subject:</span> {s.subject}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>

              <Card className="glass">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="w-5 h-5" />
                    Email Blast
                  </CardTitle>
                  <CardDescription>Send email to all members or specific membership tier</CardDescription>
                </CardHeader>
                <CardContent>
                  <Dialog open={emailBlastDialog} onOpenChange={setEmailBlastDialog}>
                    <DialogTrigger asChild>
                      <Button className="bg-rose-600 hover:bg-rose-700">
                        <Send className="w-4 h-4 mr-2" />
                        Compose Email
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Send Email Blast</DialogTitle>
                        <DialogDescription>Send announcement or update to your members</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <Input
                          placeholder="Email Subject"
                          value={emailForm.subject}
                          onChange={(e) => setEmailForm({...emailForm, subject: e.target.value})}
                        />
                        <div className="mb-4">
                          <label className="text-sm text-gray-600 mb-2 block">Email Message</label>
                          <Textarea
                            placeholder="Enter your email message here..."
                            value={emailForm.message}
                            onChange={(e) => setEmailForm({...emailForm, message: e.target.value})}
                            rows={12}
                            className="w-full p-3 border rounded-lg"
                          />
                          <p className="text-xs text-gray-500 mt-2">
                            💡 Tip: You can use basic HTML tags like &lt;b&gt;, &lt;i&gt;, &lt;br&gt;, &lt;p&gt; for formatting
                          </p>
                        </div>
                        <select
                          className="w-full p-2 border rounded"
                          value={emailForm.target_tier}
                          onChange={(e) => setEmailForm({...emailForm, target_tier: e.target.value})}
                        >
                          <option value="">All Members</option>
                          <option value="free">Free Tier Only</option>
                          <option value="basic">Basic Tier Only</option>
                          <option value="premium">Premium Tier Only</option>
                          <option value="corporate">Corporate Tier Only</option>
                        </select>
                        <Button onClick={sendEmailBlast} className="w-full bg-rose-600 hover:bg-rose-700">
                          Send Email
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>

              <Card className="glass">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Bell className="w-5 h-5" />
                        Announcements
                      </CardTitle>
                      <CardDescription>Site-wide announcements shown to all members</CardDescription>
                    </div>
                    <Dialog open={announcementDialog} onOpenChange={setAnnouncementDialog}>
                      <DialogTrigger asChild>
                        <Button className="bg-rose-600 hover:bg-rose-700">
                          <Bell className="w-4 h-4 mr-2" />
                          Create Announcement
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Create Announcement</DialogTitle>
                          <DialogDescription>Announcement will be shown on user dashboards</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <Input
                            placeholder="Title"
                            value={announcementForm.title}
                            onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                          />
                          <Textarea
                            placeholder="Message"
                            value={announcementForm.message}
                            onChange={(e) => setAnnouncementForm({...announcementForm, message: e.target.value})}
                            rows={3}
                          />
                          <select
                            className="w-full p-2 border rounded"
                            value={announcementForm.type}
                            onChange={(e) => setAnnouncementForm({...announcementForm, type: e.target.value})}
                          >
                            <option value="info">Info</option>
                            <option value="success">Success</option>
                            <option value="warning">Warning</option>
                          </select>
                          <Input
                            type="datetime-local"
                            placeholder="Expires At (optional)"
                            value={announcementForm.expires_at}
                            onChange={(e) => setAnnouncementForm({...announcementForm, expires_at: e.target.value})}
                          />
                          <Button onClick={createAnnouncement} className="w-full bg-rose-600 hover:bg-rose-700">
                            Create Announcement
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {announcements.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No announcements yet</p>
                    ) : (
                      announcements.map((ann) => (
                        <Card key={ann.id} className={`border-l-4 ${
                          ann.type === 'success' ? 'border-green-500' : 
                          ann.type === 'warning' ? 'border-orange-500' : 
                          'border-blue-500'
                        }`}>
                          <CardContent className="pt-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <h4 className="font-semibold mb-1">{ann.title}</h4>
                                <p className="text-sm text-gray-600">{ann.message}</p>
                                <div className="flex gap-2 mt-2">
                                  <Badge className={ann.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                    {ann.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                  {ann.expires_at && (
                                    <Badge variant="outline">Expires: {new Date(ann.expires_at).toLocaleDateString()}</Badge>
                                  )}
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteAnnouncement(ann.id)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Pending Partnerships */}
          <TabsContent value="partnerships">
            <div className="space-y-4">
              {pendingPartnerships.length === 0 ? (
                <Card className="glass">
                  <CardContent className="py-12 text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600">No pending partnerships to review</p>
                  </CardContent>
                </Card>
              ) : (
                pendingPartnerships.map((partnership) => (
                  <Card key={partnership.id} className="glass">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="flex items-center gap-2">
                            {partnership.partner_name}
                            <Badge className={getStatusBadge(partnership.status)}>
                              {partnership.status}
                            </Badge>
                          </CardTitle>
                          <CardDescription className="mt-1">
                            {partnership.package_type.replace('_', ' ').toUpperCase()} Package
                          </CardDescription>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-rose-600">
                            €{partnership.amount.toFixed(2)}
                          </p>
                          <p className="text-sm text-gray-500">{partnership.currency.toUpperCase()}</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-600">Payment Method</p>
                          <p className="font-medium">{partnership.payment_method}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Payment Status</p>
                          <Badge className={getStatusBadge(partnership.payment_status)}>
                            {partnership.payment_status}
                          </Badge>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Website</p>
                          <p className="font-medium">{partnership.website_url || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Created</p>
                          <p className="font-medium">{formatDate(partnership.created_at)}</p>
                        </div>
                      </div>
                      {partnership.description && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-600">Description</p>
                          <p className="text-sm">{partnership.description}</p>
                        </div>
                      )}
                      <div className="flex gap-2">
                        {partnership.status === 'pending' && (
                          <Button 
                            onClick={() => approvePartnership(partnership.id)}
                            className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                          >
                            <CheckCircle className="w-4 h-4" />
                            Approve Partnership
                          </Button>
                        )}
                        {(partnership.status === 'pending_payment' || partnership.payment_status === 'pending') && (
                          <Button 
                            onClick={() => markPartnershipPaid(partnership.id)}
                            className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                          >
                            <DollarSign className="w-4 h-4" />
                            Mark as Paid
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deletePartnership(partnership.id, partnership.partner_name)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* All Advertisements with Filters */}
          <TabsContent value="advertisements">
            <div className="space-y-4">
              {/* Filter Buttons */}
              <Card className="glass">
                <CardContent className="py-4">
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={adFilter === 'all' ? 'default' : 'outline'}
                      onClick={() => setAdFilter('all')}
                      className={adFilter === 'all' ? 'bg-rose-600 hover:bg-rose-700' : ''}
                    >
                      All ({allAds.length})
                    </Button>
                    <Button
                      variant={adFilter === 'pending' ? 'default' : 'outline'}
                      onClick={() => setAdFilter('pending')}
                      className={adFilter === 'pending' ? 'bg-yellow-600 hover:bg-yellow-700' : ''}
                    >
                      Pending ({allAds.filter(ad => ad.status === 'pending').length})
                    </Button>
                    <Button
                      variant={adFilter === 'active' ? 'default' : 'outline'}
                      onClick={() => setAdFilter('active')}
                      className={adFilter === 'active' ? 'bg-green-600 hover:bg-green-700' : ''}
                    >
                      Active ({allAds.filter(ad => ad.status === 'active' && new Date(ad.end_date) > new Date()).length})
                    </Button>
                    <Button
                      variant={adFilter === 'expired' ? 'default' : 'outline'}
                      onClick={() => setAdFilter('expired')}
                      className={adFilter === 'expired' ? 'bg-gray-600 hover:bg-gray-700' : ''}
                    >
                      Expired ({allAds.filter(ad => new Date(ad.end_date) < new Date()).length})
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Advertisements List */}
              {allAds.filter(ad => {
                if (adFilter === 'all') return true;
                if (adFilter === 'pending') return ad.status === 'pending';
                if (adFilter === 'active') return ad.status === 'active' && new Date(ad.end_date) > new Date();
                if (adFilter === 'expired') return new Date(ad.end_date) < new Date();
                return true;
              }).length === 0 ? (
                <Card className="glass">
                  <CardContent className="py-12 text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <p className="text-gray-600">No {adFilter === 'all' ? '' : adFilter} advertisements found</p>
                  </CardContent>
                </Card>
              ) : (
                allAds.filter(ad => {
                  if (adFilter === 'all') return true;
                  if (adFilter === 'pending') return ad.status === 'pending';
                  if (adFilter === 'active') return ad.status === 'active' && new Date(ad.end_date) > new Date();
                  if (adFilter === 'expired') return new Date(ad.end_date) < new Date();
                  return true;
                }).map((ad) => {
                  const isExpired = new Date(ad.end_date) < new Date();
                  return (
                    <Card key={ad.id} className="glass">
                      <CardHeader>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <CardTitle className="flex items-center gap-2 flex-wrap">
                              {ad.title}
                              <Badge className={getStatusBadge(ad.status)}>
                                {ad.status}
                              </Badge>
                              {isExpired && (
                                <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-300">
                                  Expired
                                </Badge>
                              )}
                            </CardTitle>
                            <CardDescription className="mt-1">
                              By {ad.advertiser_name} • {ad.ad_type.replace('_', ' ').toUpperCase()}
                            </CardDescription>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-rose-600">
                              €{ad.amount?.toFixed(2) || '0.00'}
                            </p>
                            <p className="text-sm text-gray-500">{ad.currency?.toUpperCase() || 'EUR'}</p>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-gray-600">Payment Method</p>
                            <p className="font-medium">{ad.payment_method || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Payment Status</p>
                            <Badge className={getStatusBadge(ad.payment_status)}>
                              {ad.payment_status}
                            </Badge>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Duration</p>
                            <p className="font-medium">{formatDate(ad.start_date)} - {formatDate(ad.end_date)}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Link</p>
                            <p className="font-medium truncate">{ad.link_url || 'N/A'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Performance</p>
                            <p className="font-medium">{ad.views || 0} views • {ad.clicks || 0} clicks</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Created</p>
                            <p className="font-medium">{formatDate(ad.created_at)}</p>
                          </div>
                        </div>
                        {ad.content && (
                          <div className="mb-4">
                            <p className="text-sm text-gray-600">Content</p>
                            <p className="text-sm line-clamp-3">{ad.content}</p>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          {ad.status === 'pending' && (
                            <Button 
                              onClick={() => approveAdvertisement(ad.id)}
                              className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve
                            </Button>
                          )}
                          {(ad.status === 'pending_payment' || ad.payment_status === 'pending') && (
                            <Button 
                              onClick={() => markAdPaid(ad.id)}
                              className="bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                            >
                              <DollarSign className="w-4 h-4" />
                              Mark as Paid
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteAdvertisement(ad.id, ad.title)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          {/* Partner Contact Inquiries */}
          <TabsContent value="contacts">
            <div className="space-y-4">
              {partnerContacts.length === 0 ? (
                <Card className="glass">
                  <CardContent className="py-12 text-center">
                    <Mail className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No partner inquiries yet</p>
                  </CardContent>
                </Card>
              ) : (
                partnerContacts.map((contact) => (
                  <Card key={contact.id} className="glass">
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle>{contact.name}</CardTitle>
                          <CardDescription className="mt-1">
                            {contact.company} • {contact.email}
                          </CardDescription>
                        </div>
                        <Badge className={getStatusBadge(contact.status)}>
                          {contact.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-600">Interest</p>
                          <p className="font-medium capitalize">{contact.interest}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Phone</p>
                          <p className="font-medium">{contact.phone || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Submitted</p>
                          <p className="font-medium">{formatDate(contact.created_at)}</p>
                        </div>
                      </div>
                      <div className="mb-4">
                        <p className="text-sm text-gray-600">Message</p>
                        <p className="text-sm">{contact.message}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => deletePartnerContact(contact.id, contact.name)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Inquiry
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* User Management */}
          <TabsContent value="users">
            <div className="space-y-4">
              {allUsers.length === 0 ? (
                <Card className="glass">
                  <CardContent className="py-12 text-center">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No users found</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="glass">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>User Management</CardTitle>
                        <CardDescription>
                          {selectedUsers.length > 0 
                            ? `${selectedUsers.length} user(s) selected` 
                            : 'Manage user roles and permissions'}
                        </CardDescription>
                      </div>
                      {selectedUsers.length > 0 && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => bulkSuspendUsers(true)}
                            className="text-orange-600 hover:bg-orange-50"
                          >
                            <Ban className="w-3 h-3 mr-1" />
                            Suspend Selected
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => bulkSuspendUsers(false)}
                            className="text-green-600 hover:bg-green-50"
                          >
                            <UserCheck className="w-3 h-3 mr-1" />
                            Activate Selected
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={bulkDeleteUsers}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete Selected
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto max-w-full">
                      <table className="w-full min-w-max">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-2 w-12">
                              <input
                                type="checkbox"
                                checked={selectedUsers.length === allUsers.length && allUsers.length > 0}
                                onChange={toggleSelectAll}
                                className="w-4 h-4 cursor-pointer"
                              />
                            </th>
                            <th className="text-left py-3 px-2 min-w-[150px]">Name</th>
                            <th className="text-left py-3 px-2 min-w-[200px]">Email</th>
                            <th className="text-left py-3 px-2 min-w-[120px]">Membership</th>
                            <th className="text-left py-3 px-2 min-w-[100px]">Role</th>
                            <th className="text-left py-3 px-2 min-w-[450px]">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allUsers.map((u) => (
                            <tr key={u.id} className={`border-b hover:bg-gray-50 ${selectedUsers.includes(u.id) ? 'bg-blue-50' : ''}`}>
                              <td className="py-3 px-2">
                                <input
                                  type="checkbox"
                                  checked={selectedUsers.includes(u.id)}
                                  onChange={() => toggleUserSelect(u.id)}
                                  className="w-4 h-4 cursor-pointer"
                                />
                              </td>
                              <td className="py-3 px-2">{u.full_name}</td>
                              <td className="py-3 px-2 text-sm text-gray-600">{u.email}</td>
                              <td className="py-3 px-2">
                                <Badge variant="outline" className="capitalize">
                                  {u.membership_tier || 'basic'}
                                </Badge>
                              </td>
                              <td className="py-3 px-2">
                                <Badge 
                                  className={
                                    u.role === 'admin' 
                                      ? 'bg-rose-100 text-rose-800' 
                                      : u.role === 'partner'
                                      ? 'bg-purple-100 text-purple-800'
                                      : 'bg-gray-100 text-gray-800'
                                  }
                                >
                                  {u.role || 'member'}
                                </Badge>
                              </td>
                              <td className="py-3 px-2">
                                <div className="flex flex-wrap gap-1">
                                  {u.role !== 'admin' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => changeUserRole(u.email, 'admin')}
                                      className="text-rose-600 hover:bg-rose-50 text-xs"
                                    >
                                      Admin
                                    </Button>
                                  )}
                                  {u.role !== 'partner' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => changeUserRole(u.email, 'partner')}
                                      className="text-purple-600 hover:bg-purple-50 text-xs"
                                    >
                                      Partner
                                    </Button>
                                  )}
                                  {u.role && u.role !== 'member' && (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => changeUserRole(u.email, 'member')}
                                      className="text-gray-600 hover:bg-gray-50 text-xs"
                                    >
                                      Member
                                    </Button>
                                  )}
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => suspendUser(u.id, u.full_name, u.is_suspended)}
                                    className={`text-xs ${u.is_suspended ? "text-green-600 hover:bg-green-50" : "text-orange-600 hover:bg-orange-50"}`}
                                  >
                                    {u.is_suspended ? <UserCheck className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => deleteUser(u.id, u.full_name)}
                                    className="text-red-600 hover:bg-red-50 text-xs"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Testimonials Management */}
          <TabsContent value="testimonials">
            <div className="mb-4">
              <Dialog open={testimonialDialog} onOpenChange={setTestimonialDialog}>
                <DialogTrigger asChild>
                  <Button 
                    onClick={() => {
                      setEditingTestimonial(null);
                      setTestimonialForm({ quote: '', author: '', role: '', order: 0 });
                    }}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Add Testimonial
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{editingTestimonial ? 'Edit' : 'Add'} Testimonial</DialogTitle>
                    <DialogDescription>
                      {editingTestimonial ? 'Update' : 'Create'} a testimonial for the landing page
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <Textarea
                      placeholder="Quote"
                      value={testimonialForm.quote}
                      onChange={(e) => setTestimonialForm({...testimonialForm, quote: e.target.value})}
                      rows={3}
                    />
                    <Input
                      placeholder="Author Name"
                      value={testimonialForm.author}
                      onChange={(e) => setTestimonialForm({...testimonialForm, author: e.target.value})}
                    />
                    <Input
                      placeholder="Author Role/Title"
                      value={testimonialForm.role}
                      onChange={(e) => setTestimonialForm({...testimonialForm, role: e.target.value})}
                    />
                    <Input
                      type="number"
                      placeholder="Display Order (0-100)"
                      value={testimonialForm.order}
                      onChange={(e) => setTestimonialForm({...testimonialForm, order: parseInt(e.target.value) || 0})}
                    />
                    <Button onClick={handleTestimonialSubmit} className="w-full bg-rose-600 hover:bg-rose-700">
                      {editingTestimonial ? 'Update' : 'Create'} Testimonial
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {testimonials.length === 0 ? (
                <Card className="glass">
                  <CardContent className="py-12 text-center">
                    <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No testimonials yet</p>
                    <p className="text-sm text-gray-500 mt-2">Members can submit testimonials from their dashboard</p>
                  </CardContent>
                </Card>
              ) : (
                <>
                  {/* Pending Testimonials */}
                  {testimonials.filter(t => !t.is_approved).length > 0 && (
                    <div className="space-y-2">
                      <h3 className="font-semibold text-gray-700">Pending Approval ({testimonials.filter(t => !t.is_approved).length})</h3>
                      {testimonials.filter(t => !t.is_approved).map((t) => (
                        <Card key={t.id} className="glass border-l-4 border-yellow-500">
                          <CardContent className="pt-4">
                            <div className="flex justify-between items-start gap-4">
                              <div className="flex-1">
                                <p className="text-gray-700 italic mb-2">"{t.quote}"</p>
                                <p className="text-sm"><span className="font-semibold">Author:</span> {t.author}</p>
                                <p className="text-sm"><span className="font-semibold">Role:</span> {t.role}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Submitted {new Date(t.created_at).toLocaleDateString()}
                                </p>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => approveTestimonial(t.id, true)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => approveTestimonial(t.id, false)}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <XCircle className="w-4 h-4 mr-1" />
                                  Reject
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}

                  {/* Approved Testimonials */}
                  {testimonials.filter(t => t.is_approved).length > 0 && (
                    <div className="space-y-2 mt-6">
                      <h3 className="font-semibold text-gray-700">Approved ({testimonials.filter(t => t.is_approved).length})</h3>
                      {testimonials.filter(t => t.is_approved).map((t) => (
                        <Card key={t.id} className="glass border-l-4 border-green-500">
                          <CardContent className="pt-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="text-gray-700 italic mb-2">"{t.quote}"</p>
                                <p className="text-sm"><span className="font-semibold">Author:</span> {t.author}</p>
                                <p className="text-sm"><span className="font-semibold">Role:</span> {t.role}</p>
                                <Badge className="mt-2 bg-green-100 text-green-800">✓ Visible on Landing Page</Badge>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingTestimonial(t);
                                    setTestimonialForm({
                                      quote: t.quote,
                                      author: t.author,
                                      role: t.role,
                                      order: t.order || 0
                                    });
                                    setTestimonialDialog(true);
                                  }}
                                  className="text-blue-600 hover:bg-blue-50"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => deleteTestimonial(t.id)}
                                  className="text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </TabsContent>

          {/* Member Testimonials Management */}
          <TabsContent value="member-testimonials">
            <Card className="glass">
              <CardHeader>
                <CardTitle>Member Testimonials (Peer-to-Peer)</CardTitle>
                <CardDescription>Manage testimonials that members give to each other</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {memberTestimonials.length === 0 ? (
                    <div className="py-12 text-center">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">No member testimonials yet</p>
                      <p className="text-sm text-gray-500 mt-2">Members can give testimonials to each other on their profile pages</p>
                    </div>
                  ) : (
                    memberTestimonials.map((t) => (
                      <Card key={t.id} className={`border-l-4 ${t.is_approved ? 'border-green-500' : 'border-yellow-500'}`}>
                        <CardContent className="pt-4">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <p className="text-gray-700 italic mb-3">"{t.message}"</p>
                              <div className="space-y-1 text-sm">
                                <p><span className="font-semibold">From:</span> {t.from_user_name}</p>
                                <p><span className="font-semibold">To:</span> User ID {t.to_user_id.substring(0, 8)}...</p>
                                {t.relationship && (
                                  <p><span className="font-semibold">Relationship:</span> {t.relationship}</p>
                                )}
                                <p className="text-xs text-gray-500">
                                  {new Date(t.created_at).toLocaleDateString()} at {new Date(t.created_at).toLocaleTimeString()}
                                </p>
                              </div>
                              <div className="flex gap-2 mt-3">
                                <Badge className={t.is_approved ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                                  {t.is_approved ? '✓ Approved' : '⏳ Pending Approval'}
                                </Badge>
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              {!t.is_approved && (
                                <Button
                                  size="sm"
                                  onClick={() => approveMemberTestimonial(t.id)}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Approve
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => deleteMemberTestimonial(t.id)}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Management */}
          <TabsContent value="events">
            <div className="space-y-4">
              <Button onClick={() => navigate('/events')} className="bg-rose-600 hover:bg-rose-700">
                <Calendar className="w-4 h-4 mr-2" />
                Create New Event
              </Button>
              
              {allEvents.length === 0 ? (
                <Card className="glass">
                  <CardContent className="py-12 text-center">
                    <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No events yet</p>
                  </CardContent>
                </Card>
              ) : (
                allEvents.map((event) => (
                  <Card key={event.id} className="glass">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">{event.title}</h3>
                          <p className="text-gray-600 mb-2">{event.description}</p>
                          <div className="flex gap-4 text-sm text-gray-500">
                            <span>📅 {new Date(event.date).toLocaleDateString()}</span>
                            <span>📍 {event.location}</span>
                            <span>👥 {event.attendees?.length || 0} attendees</span>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteEvent(event.id, event.title)}
                          className="text-red-600 hover:bg-red-50 ml-4"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Businesses Moderation */}
          <TabsContent value="businesses">
            <div className="space-y-4">
              {allBusinesses.length === 0 ? (
                <Card className="glass">
                  <CardContent className="py-12 text-center">
                    <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No businesses showcased yet</p>
                  </CardContent>
                </Card>
              ) : (
                allBusinesses.map((business) => (
                  <Card key={business.id} className="glass">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-2">{business.name}</h3>
                          <p className="text-gray-600 mb-2">{business.description}</p>
                          <div className="flex gap-2 flex-wrap">
                            {business.industry && <Badge variant="outline">{business.industry}</Badge>}
                            {business.website && (
                              <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-sm text-rose-600 hover:underline">
                                🔗 Website
                              </a>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-2">Owner: {business.owner_name}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteBusiness(business.id, business.name)}
                          className="text-red-600 hover:bg-red-50 ml-4"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Opportunities Moderation */}
          <TabsContent value="opportunities">
            <div className="space-y-4">
              {allOpportunities.length === 0 ? (
                <Card className="glass">
                  <CardContent className="py-12 text-center">
                    <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No opportunities posted yet</p>
                  </CardContent>
                </Card>
              ) : (
                allOpportunities.map((opp) => (
                  <Card key={opp.id} className="glass">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{opp.title}</h3>
                            <Badge variant="outline" className="capitalize">{opp.type}</Badge>
                          </div>
                          <p className="text-gray-600 mb-2">{opp.description}</p>
                          <div className="flex items-center gap-4 mt-3">
                            <p className="text-sm text-gray-500">Posted by: <span className="font-medium">{opp.poster_name}</span></p>
                            <p className="text-sm text-rose-600 font-medium">
                              👥 {opp.applicants?.length || 0} {(opp.applicants?.length || 0) === 1 ? 'Applicant' : 'Applicants'}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteOpportunity(opp.id, opp.title)}
                          className="text-red-600 hover:bg-red-50 ml-4"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Employees Tab */}
          <TabsContent value="employees">
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Employee Management</h2>
                <Button onClick={() => openEmployeeDialog()} className="bg-rose-600 hover:bg-rose-700">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Employee
                </Button>
              </div>

              {employees.length === 0 ? (
                <Card className="glass">
                  <CardContent className="py-12 text-center">
                    <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No employees added yet</p>
                    <Button onClick={() => openEmployeeDialog()} className="mt-4 bg-rose-600 hover:bg-rose-700">
                      Add Your First Employee
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {employees.map((employee) => (
                    <Card key={employee.id} className="glass">
                      <CardContent className="pt-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{employee.full_name}</h3>
                              <Badge variant={employee.status === 'active' ? 'default' : 'secondary'} className={employee.status === 'active' ? 'bg-green-100 text-green-800' : ''}>
                                {employee.status}
                              </Badge>
                              <Badge 
                                variant="outline" 
                                className={
                                  employee.role === 'admin' ? 'bg-red-100 text-red-800 border-red-300' :
                                  employee.role === 'manager' ? 'bg-blue-100 text-blue-800 border-blue-300' :
                                  employee.role === 'viewer' ? 'bg-gray-100 text-gray-800 border-gray-300' :
                                  'bg-purple-100 text-purple-800 border-purple-300'
                                }
                              >
                                {employee.role || 'employee'}
                              </Badge>
                              <Badge 
                                variant="outline" 
                                className={
                                  employee.account_status === 'active' ? 'bg-green-100 text-green-800 border-green-300' :
                                  employee.account_status === 'invited' ? 'bg-yellow-100 text-yellow-800 border-yellow-300' :
                                  'bg-gray-100 text-gray-800 border-gray-300'
                                }
                              >
                                {employee.account_status === 'active' ? '✓ Active Account' :
                                 employee.account_status === 'invited' ? '📧 Invited' :
                                 '⏳ Pending'}
                              </Badge>
                            </div>
                            <p className="text-gray-600 mb-1">
                              <strong>Position:</strong> {employee.position}
                              {employee.department && <span> • <strong>Department:</strong> {employee.department}</span>}
                            </p>
                            <p className="text-gray-600 mb-1">
                              <strong>Email:</strong> {employee.email}
                              {employee.phone && <span> • <strong>Phone:</strong> {employee.phone}</span>}
                            </p>
                            {employee.start_date && (
                              <p className="text-sm text-gray-500">
                                <strong>Start Date:</strong> {new Date(employee.start_date).toLocaleDateString()}
                              </p>
                            )}
                            {employee.salary && (
                              <p className="text-sm text-gray-500">
                                <strong>Salary:</strong> €{Number(employee.salary).toLocaleString()}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            {employee.account_status !== 'active' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => sendInvitation(employee.id, employee.full_name, employee.email)}
                                className="text-green-600 hover:bg-green-50"
                                title={employee.account_status === 'invited' ? 'Resend Invitation' : 'Send Invitation'}
                              >
                                <Mail className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEmployeeDialog(employee)}
                              className="hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteEmployee(employee.id, employee.full_name)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Employee Dialog */}
          <Dialog open={employeeDialog} onOpenChange={setEmployeeDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingEmployee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
                <DialogDescription>
                  {editingEmployee ? 'Update employee information' : 'Enter details for the new employee'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Full Name *</label>
                    <Input
                      value={employeeForm.full_name}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, full_name: e.target.value })}
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Email *</label>
                    <Input
                      type="email"
                      value={employeeForm.email}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, email: e.target.value })}
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Position *</label>
                    <Input
                      value={employeeForm.position}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, position: e.target.value })}
                      placeholder="Software Engineer"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Access Role *</label>
                    <select
                      value={employeeForm.role}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, role: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                    >
                      <option value="employee">Employee (Standard Access)</option>
                      <option value="manager">Manager (Department Access)</option>
                      <option value="admin">Admin (Full Access)</option>
                      <option value="viewer">Viewer (Read-Only Access)</option>
                    </select>
                  </div>
                </div>

                {/* Super Admin Checkbox - Only show for Admin role */}
                {employeeForm.role === 'admin' && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="isSuperAdmin"
                        checked={employeeForm.is_super_admin || false}
                        onChange={(e) => setEmployeeForm({ ...employeeForm, is_super_admin: e.target.checked })}
                        className="mt-1 w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <div className="flex-1">
                        <label htmlFor="isSuperAdmin" className="text-sm font-bold text-orange-800 cursor-pointer flex items-center gap-2">
                          🛡️ Grant Super Admin Status
                        </label>
                        <p className="text-xs text-orange-700 mt-1">
                          Super Admins have protected accounts that cannot be deleted, suspended, or demoted by other admins. Only grant this to trusted platform owners.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Department</label>
                    <Input
                      value={employeeForm.department}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, department: e.target.value })}
                      placeholder="Engineering"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Phone</label>
                    <Input
                      value={employeeForm.phone}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, phone: e.target.value })}
                      placeholder="+1234567890"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Start Date</label>
                    <Input
                      type="date"
                      value={employeeForm.start_date}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, start_date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Salary (€)</label>
                    <Input
                      type="number"
                      value={employeeForm.salary}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, salary: e.target.value })}
                      placeholder="50000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Status</label>
                    <select
                      value={employeeForm.status}
                      onChange={(e) => setEmployeeForm({ ...employeeForm, status: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="on_leave">On Leave</option>
                    </select>
                  </div>
                </div>

                {!editingEmployee && (
                  <div className="flex items-center gap-2 pt-4 pb-2 border-t">
                    <input
                      type="checkbox"
                      id="sendInvitationNow"
                      checked={sendInvitationNow}
                      onChange={(e) => setSendInvitationNow(e.target.checked)}
                      className="w-4 h-4 text-rose-600 border-gray-300 rounded focus:ring-rose-500"
                    />
                    <label htmlFor="sendInvitationNow" className="text-sm font-medium cursor-pointer">
                      📧 Send invitation email immediately
                    </label>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setEmployeeDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={saveEmployee} className="bg-rose-600 hover:bg-rose-700">
                    {editingEmployee ? 'Update Employee' : 'Add Employee'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminPanel;
